import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  CardContent,
  DesktopIcon,
  FolderOpenEmptyIcon,
  Grid,
  Paper,
  LockIcon,
  List,
  ListItemText,
  ListItemIcon,
  ListItem,
  Link,
  Typography,
  ClockIcon,
  MailIcon,
} from '@availity/element';
import envVar from '@availity/env-var';

import { AvailityHeaderLogo, Footer } from '@/components';
import { useFRCallback } from '@/hooks';

const legacySupportUrl = 'https://availity.tfaforms.net/f/lockout';
const qaSupportUrl = 'https://availity.tfaforms.net/26';
const supportUrl = envVar({
  prod: legacySupportUrl,
  qap: legacySupportUrl,
  qa: qaSupportUrl,
  local: legacySupportUrl,
});

const EmailSent = ({ stepToRender }) => {
  const { suspendedTextOutputMessage = {} } = useFRCallback(stepToRender);

  const { masked = '', journey = '' } = suspendedTextOutputMessage;
  const formattedMaskedEmail = useMemo(() => masked?.replaceAll(/["']+/g, ''), [masked]);

  const emailPrompt = useMemo(() => {
    const output =
      journey && journey?.toLowerCase() === 'emailverification' ? (
        <Box>
          <Typography variant="subtitle1">Your verification email has been sent to:</Typography>
          <Typography variant="subtitle1">
            <Box sx={{ fontWeight: 'bold' }}>{formattedMaskedEmail}</Box>
          </Typography>
        </Box>
      ) : (
        <Typography variant="subtitle1">
          If an email address is associated with your account, you will receive instructions with next steps.
        </Typography>
      );

    return output;
  }, [journey, formattedMaskedEmail]);

  const nextStepsContent = useMemo(() => {
    let output;
    if (journey) {
      const lowercaseJourney = journey?.toLowerCase();
      switch (lowercaseJourney) {
        case 'emailverification': {
          output = (
            <List
              component="ul"
              sx={{
                listStylePosition: 'outside',
                paddingLeft: 3,
              }}
              disablePadding
            >
              <ListItem disableGutters divider={false}>
                <ListItemIcon>
                  <ClockIcon fontSize="large" />
                </ListItemIcon>
                <ListItemText primary="The email link will expire in 24 hours." />
              </ListItem>
              <ListItem disableGutters divider={false}>
                <ListItemIcon>
                  <MailIcon fontSize="large" />
                </ListItemIcon>
                <ListItemText
                  primary="If you've received multiple verification emails, use
                the most recently sent."
                />
              </ListItem>
              <ListItem disableGutters divider={false}>
                <ListItemIcon>
                  <FolderOpenEmptyIcon fontSize="large" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box component="span">
                      It might be in your spam folder. Check for an email from <em>do-not-reply@availity.com</em>
                    </Box>
                  }
                />
              </ListItem>
            </List>
          );
          break;
        }
        default: {
          output = (
            <List
              component="ul"
              sx={{
                listStylePosition: 'outside',
                paddingLeft: 3,
              }}
              disablePadding
            >
              <ListItem disableGutters divider={false}>
                <ListItemIcon>
                  <FolderOpenEmptyIcon fontSize="large" />
                </ListItemIcon>
                <ListItemText
                  primary="It might be in your spam folder."
                  secondary={
                    <Box component="span">
                      Check for an email from <em>do-not-reply@availity.com</em>
                    </Box>
                  }
                />
              </ListItem>
              <ListItem disableGutters divider={false}>
                <ListItemIcon>
                  <LockIcon fontSize="large" />
                </ListItemIcon>
                <ListItemText
                  primary="Your account may be locked."
                  secondary="Contact your administrator to unlock your account."
                />
              </ListItem>
              <ListItem disableGutters divider={false}>
                <ListItemIcon>
                  <DesktopIcon fontSize="large" />
                </ListItemIcon>
                <ListItemText
                  primary="Need further assistance?"
                  secondary={
                    <Link href={supportUrl} target="_blank" loadApp={false} fontSize="small">
                      Get Availity support.
                    </Link>
                  }
                />
              </ListItem>
            </List>
          );
        }
      }
    }

    return output;
  }, [journey]);

  return (
    <Grid
      id="email-sent-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid size={{ xs: 12, sm: 12, md: 6 }} display="flex" flexDirection="column" alignItems="center" flexGrow={1}>
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper variant="elevation" elevation={6} sx={{ width: { xs: '350px', sm: '500px', md: '700px' } }}>
            <CardContent>
              <Typography variant="h1" mb={2}>
                Check your email.
              </Typography>
              {emailPrompt}
              {nextStepsContent}
            </CardContent>
          </Paper>
        </Grid>
      </Grid>
      <Footer />
    </Grid>
  );
};

EmailSent.propTypes = {
  stepToRender: PropTypes.object.isRequired,
};

export default EmailSent;
